import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import {
  useGetInvestorProfileForHybridQuery,
  useUpdateIraAccountMutation,
} from "../../graphql/supergraphHooks";
import { readBooleanProperty } from "../../utils/json_utils";
import { EntitySurveyPage } from "../EntitySurveyPage";
import { provideFullName } from "../ProfileTree/utils";

export const HybridCategorizationPage = () => {
  const history = useHistory();
  const { caseId, basePath } = useComplianceCaseContext();

  const iraQueryResult = useGetInvestorProfileForHybridQuery({
    variables: { caseId: caseId },
    skip: !caseId,
    fetchPolicy: "no-cache",
  });

  const [updateIraAccount, { loading: isUpdating }] = useUpdateIraAccountMutation({
    refetchQueries: ["GetComplianceCase"],
    awaitRefetchQueries: true,
  });

  const investingProfile = iraQueryResult.data?.complianceCase?.investingProfile;

  const getIsIraAnswer = (metaData: unknown): string => {
    const isIraAccount = readBooleanProperty(metaData, "is_ira_account", null);
    if (isIraAccount === null) {
      return "";
    }
    return isIraAccount ? "Yes" : "No";
  };

  const iraSingleQuestionList = useMemo(
    () => [
      {
        id: "1",
        question: "Is this account an IRA or non-US retirement account?",
        answer: getIsIraAnswer(investingProfile?.metaData),
      },
    ],
    [investingProfile?.metaData],
  );

  const displayProfileName = useMemo(() => {
    /**
     * Get name in a non-standard way because we are not sure if the profile is an institution or individual with this hybrid ('ira') account type
     */
    return investingProfile?.legalName?.trim() ?? provideFullName(investingProfile);
  }, [investingProfile]);

  return (
    <QueryLoader result={iraQueryResult}>
      <EntitySurveyPage
        questions={iraSingleQuestionList}
        entityName={displayProfileName}
        hideBackButton={true}
        isUpdating={isUpdating}
        onCompleted={async (values) => {
          const isIraAccount = !!values.updatedQuestions.find(
            (question) => question.answer === "Yes",
          );

          await updateIraAccount({
            variables: {
              profileId: investingProfile?.id || 0,
              metaData: { is_ira_account: isIraAccount },
              caseId: caseId || 0,
            },
            onCompleted: () => {
              toast.success("Answer recorded successfully");
              history.push(`${basePath}`);
            },
            onError: () => {
              toast.error(`Error recording answer`);
            },
          });
        }}
        onBack={() => {
          history.goBack();
        }}
      />
    </QueryLoader>
  );
};
